import * as _ from 'lodash';
import JsonApiRelationship from './jsonapi-relationship';
import JsonApiResource from './jsonapi-resource';
import JsonApiReference from './jsonapi-reference';
import ReferenceResolver from './reference-resolver';

interface NameMap {
  [localName: string]: string;
}

enum Direction {
  LocalOnly,
  Read,
  ReadWrite
}

abstract class BaseResource {
  id: number = null;
  type: string = null;
  meta: any;
  private _unpacked: boolean = false;

  get unpacked(): boolean {
    return this._unpacked;
  }

  protected readonly attributes: NameMap = {};
  protected readonly relationships: NameMap = {};
  static reverseNames: any = {};

  constructor(init?: Partial<BaseResource>, type?: string) {
    if (type) {
      this.type = type;
    }
  }

  pack(): JsonApiResource {
    let result: JsonApiResource = new JsonApiResource({
      id: this.id,
      type: this.type,
      attributes: {}
    });

    _.chain(_.keys(this))
      .filter(name => _.isUndefined(result[name]))
      .each(name => {
        const direction = this.getDirection(name);
        if (direction !== Direction.ReadWrite) return;
        if (name[0] === '_' || name === 'meta') return;

        const apiName: string = this.getApiName(name);
        const item: any = this[name];

        if (item instanceof BaseResource) {
          result.addRelated(item, apiName);
        } else if (item instanceof Array) {
          result.addRelatedArray(item, apiName);
        } else if (
          typeof item === 'string' ||
          typeof item === 'number' ||
          typeof item === 'boolean' ||
          typeof item === 'object' ||
          _.isNil(item)
        ) {
          result.addAttribute(item, apiName);
        } else {
          console.error(`Don't know how to pack data: ${name} = '${item}'`);
        }
      })
      .value();

    return result;
  }

  packAsReference(): JsonApiReference {
    return new JsonApiReference({
      id: this.id,
      type: this.type
    });
  }

  unpack(init: JsonApiResource, resolver: ReferenceResolver): void;
  unpack(init: JsonApiReference, resolver: ReferenceResolver): void;
  unpack(init: any, resolver: ReferenceResolver): void {
    this.id = init.id;
    this.type = init.type;

    if (init instanceof JsonApiResource) {
      this._unpacked = true;
      _.each(init.attributes, (item, apiName) => {
        const name = this.getLocalName(apiName);
        this[name] = item;
      });
      _.each(init.relationships, (relation: JsonApiRelationship, apiName: string) => {
        const name = this.getLocalName(apiName);

        if (relation.data) {
          this[name] = resolver(relation.data);
        }
      });
    }
  }

  private getApiName(name: string): string {
    const attributeName: string = (this.attributes[name] || '').slice(1);
    const relationshipName: string = (this.relationships[name] || '').slice(1);

    return attributeName || relationshipName || this.skewer(name);
  }

  private getDirection(name: string): Direction {
    let result: Direction = Direction.ReadWrite;
    const config: string = this.attributes[name] || this.relationships[name] || '=';

    switch (config[0]) {
      case '<':
        return Direction.Read;
      case '!':
        return Direction.LocalOnly;
      default:
        return Direction.ReadWrite;
    }
  }

  private getLocalName(apiName: string): string {
    if (_.isUndefined(BaseResource.reverseNames[this.type])) {
      BaseResource.reverseNames[this.type] = <NameMap>_.chain({})
        .extend(this.attributes, this.relationships)
        .mapValues((item: string) => item.slice(1))
        .invert()
        .value();
    }

    if (!_.has(BaseResource.reverseNames[this.type], apiName)) {
      BaseResource.reverseNames[this.type][apiName] = this.camelize(apiName);
    }

    return BaseResource.reverseNames[this.type][apiName];
  }

  /// Turns a camel case name into a skewered one
  private skewer(name: string): string {
    return name.replace(/([A-Z])/g, i => `-${i.toLowerCase()}`);
  }

  /// Turns shishkebob case into camel
  private camelize(name: string): string {
    return name.replace(/\-([a-z])/g, (m, g) => `${g.toUpperCase()}`);
  }

  protected initialize(init?: Partial<BaseResource>) {
    if (init instanceof Object) {
      _.assign(this, init);
    }
  }
}

export default BaseResource;



// WEBPACK FOOTER //
// ./common/models/base-resource.ts